import React, { useState, useEffect } from 'react';
import { Template, TemplateElement, TemplateEditorProps, TEMPLATE_CATEGORIES, DIFFICULTY_LEVELS } from './types';
import TemplateElementComponent from './TemplateElement';
import VoiceTemplateGenerator from './VoiceTemplateGenerator';
import TemplatePreview from './TemplatePreview';

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  isEditing,
  isDirty,
  onSave,
  onCancel,
  onDelete,
  onElementAdd,
  onElementUpdate,
  onElementDelete,
  onElementReorder
}) => {
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showVoiceGenerator, setShowVoiceGenerator] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Sync with prop changes
  useEffect(() => {
    if (template) {
      setEditingTemplate({ ...template });
    } else if (isEditing) {
      // New template
      setEditingTemplate({
        id: '',
        name: '',
        description: '',
        voicePrompt: '',
        elements: [],
        category: 'general',
        metadata: {
          estimatedDuration: '',
          difficulty: 'beginner',
          industry: '',
          tags: []
        },
        isActive: true,
        usageCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } else {
      setEditingTemplate(null);
    }
    setShowVoiceGenerator(false);
    setDeleteConfirm(false);
  }, [template, isEditing]);

  const handleFieldChange = (field: keyof Template, value: any) => {
    if (!editingTemplate) return;
    
    setEditingTemplate(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleMetadataChange = (field: string, value: any) => {
    if (!editingTemplate) return;

    setEditingTemplate(prev => prev ? {
      ...prev,
      metadata: { ...prev.metadata, [field]: value }
    } : null);
  };

  const handleSave = async () => {
    if (!editingTemplate || saving) return;

    try {
      setSaving(true);
      await onSave(editingTemplate);
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingTemplate?.id || saving) return;

    try {
      setSaving(true);
      await onDelete(editingTemplate.id);
      setDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddElement = () => {
    if (!editingTemplate) return;

    const newElement: Omit<TemplateElement, 'id'> = {
      type: 'question',
      content: '',
      order: editingTemplate.elements.length + 1,
      category: 'discovery',
      required: false,
      placeholder: 'Enter your question here...'
    };

    onElementAdd(newElement);
    
    // Update local state
    setEditingTemplate(prev => prev ? {
      ...prev,
      elements: [...prev.elements, { ...newElement, id: `temp-${Date.now()}` }]
    } : null);
  };

  const handleElementUpdate = (elementId: string, updates: Partial<TemplateElement>) => {
    if (!editingTemplate) return;

    onElementUpdate(elementId, updates);
    
    // Update local state
    setEditingTemplate(prev => prev ? {
      ...prev,
      elements: prev.elements.map(el => el.id === elementId ? { ...el, ...updates } : el)
    } : null);
  };

  const handleElementDelete = (elementId: string) => {
    if (!editingTemplate) return;

    onElementDelete(elementId);
    
    // Update local state and reorder
    const filteredElements = editingTemplate.elements.filter(el => el.id !== elementId);
    const reorderedElements = filteredElements.map((el, index) => ({ ...el, order: index + 1 }));
    
    setEditingTemplate(prev => prev ? {
      ...prev,
      elements: reorderedElements
    } : null);
  };

  const handleElementReorder = (elementId: string, direction: 'up' | 'down') => {
    if (!editingTemplate) return;

    const elements = [...editingTemplate.elements];
    const elementIndex = elements.findIndex(el => el.id === elementId);
    
    if (elementIndex === -1) return;
    
    const newIndex = direction === 'up' ? elementIndex - 1 : elementIndex + 1;
    
    if (newIndex < 0 || newIndex >= elements.length) return;

    // Swap elements
    [elements[elementIndex], elements[newIndex]] = [elements[newIndex], elements[elementIndex]];
    
    // Update order
    const reorderedElements = elements.map((el, index) => ({ ...el, order: index + 1 }));
    
    setEditingTemplate(prev => prev ? {
      ...prev,
      elements: reorderedElements
    } : null);

    onElementReorder(elementId, newIndex + 1);
  };

  const handleVoiceGenerated = (generatedTemplate: any) => {
    setEditingTemplate(prev => prev ? {
      ...prev,
      ...generatedTemplate,
      id: prev.id || '',
      createdAt: prev.createdAt,
      updatedAt: prev.updatedAt,
      usageCount: prev.usageCount,
      isActive: prev.isActive
    } : null);
    setShowVoiceGenerator(false);
  };

  if (!isEditing) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🎙️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Neural Template Builder</h3>
          <p className="text-gray-600 mb-4">Select a template to edit or create a new one using voice commands</p>
        </div>
      </div>
    );
  }

  if (!editingTemplate) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white h-full">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {template?.id ? 'Edit Template' : 'New Template'}
          </h2>
          <div className="flex items-center space-x-2">
            {!showVoiceGenerator && (
              <button
                onClick={() => setShowVoiceGenerator(true)}
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                title="Generate with Voice"
              >
                🧠 Neural Mode
              </button>
            )}
            {template?.id && (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                disabled={saving}
              >
                Delete
              </button>
            )}
            <button
              onClick={onCancel}
              className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !editingTemplate.name.trim()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {isDirty && (
          <div className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
            ⚠️ You have unsaved changes
          </div>
        )}
      </div>

      {/* Unified Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
        {/* Voice Generator */}
        {showVoiceGenerator && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg">
            <div className="p-4">
              <VoiceTemplateGenerator
                onTemplateGenerated={handleVoiceGenerated}
                isGenerating={false}
                generationError={null}
                onClearError={() => {}}
              />
              <button
                onClick={() => setShowVoiceGenerator(false)}
                className="mt-2 text-sm text-gray-600 hover:text-gray-800"
              >
                ← Back to manual editing
              </button>
            </div>
          </div>
        )}

        {/* Basic Info - Two Column Layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Template Name - Full Row */}
            <div>
              <input
                type="text"
                value={editingTemplate.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Template Name (e.g., 401k Consultation Template)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category, Complexity, Duration - One Row */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <input
                  type="text"
                  value={editingTemplate.category}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  placeholder="Category"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <select
                  value={editingTemplate.metadata.difficulty || 'beginner'}
                  onChange={(e) => handleMetadataChange('difficulty', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="beginner">Standard</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div>
                <input
                  type="text"
                  value={editingTemplate.metadata.estimatedDuration || ''}
                  onChange={(e) => handleMetadataChange('estimatedDuration', e.target.value)}
                  placeholder="Duration"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <textarea
              value={editingTemplate.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Brief description of when to use this template..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Template Elements */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-medium text-gray-900">Template Elements</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowPreview(true)}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                disabled={editingTemplate.elements.length === 0}
                title="Preview Template"
              >
                👁️ Preview
              </button>
              <button
                onClick={handleAddElement}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                + Add Element
              </button>
            </div>
          </div>

          {editingTemplate.elements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📝</div>
              <p>No elements yet. Add your first question or statement!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {editingTemplate.elements.map((element, index) => (
                <TemplateElementComponent
                  key={element.id}
                  element={element}
                  isEditing={true}
                  onUpdate={(updates) => handleElementUpdate(element.id, updates)}
                  onDelete={() => handleElementDelete(element.id)}
                  onMoveUp={() => handleElementReorder(element.id, 'up')}
                  onMoveDown={() => handleElementReorder(element.id, 'down')}
                  canMoveUp={index > 0}
                  canMoveDown={index < editingTemplate.elements.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Template Preview Modal */}
      {showPreview && editingTemplate && (
        <TemplatePreview
          template={editingTemplate}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Template</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete "{editingTemplate.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor; 